import { TaskCompletionDetailsTableRow } from '@lib/components/TaskCompletions/types';
import getWorstValidityHint from './getWorstValidityHint';
import { CompareCompletionColumn, CompareCompletionRow } from './types';

// A new form version re-creates every question and field with fresh uuids,
// so `field.id` only matches answers within one version. Across versions the
// same question is matched by what the answer snapshots — position, name and
// kind — plus its occurrence index, for a question that repeats a name/kind.
function getVersionAgnosticKeys(rows: TaskCompletionDetailsTableRow[]) {
  const occurrences = new Map<string, number>();
  return rows.map((row) => {
    const baseKey = [row.questionPosition, row.questionName, row.kind].join(
      '|',
    );
    const occurrence = occurrences.get(baseKey) ?? 0;
    occurrences.set(baseKey, occurrence + 1);
    return `${baseKey}|${occurrence}`;
  });
}

export default function alignCompareRows(
  columns: CompareCompletionColumn[],
): CompareCompletionRow[] {
  const loadedVersionIds = new Set(
    columns
      .filter(({ completion }) => !!completion)
      .map(({ completion }) => completion?.task?.formTemplateVersion?.id),
  );
  const isSingleVersion = loadedVersionIds.size <= 1;

  const rowsById = new Map<string, CompareCompletionRow>();
  const orderedIds: string[] = [];

  columns.forEach((column, columnIndex) => {
    const keys = isSingleVersion
      ? column.rows.map(({ id }) => id)
      : getVersionAgnosticKeys(column.rows);
    column.rows.forEach((fieldRow, rowIndex) => {
      const rowId = keys[rowIndex];
      let row = rowsById.get(rowId);
      if (!row) {
        row = {
          id: rowId,
          questionName: fieldRow.questionName,
          kind: fieldRow.kind,
          options: fieldRow.options,
          columnItems: new Array(columns.length).fill(undefined),
        };
        rowsById.set(rowId, row);
        orderedIds.push(rowId);
      }
      row.columnItems[columnIndex] = fieldRow;
    });
  });

  return orderedIds.map((id) => {
    const row = rowsById.get(id) as CompareCompletionRow;
    return {
      ...row,
      worstValidityHint: getWorstValidityHint(
        row.columnItems.map((columnItem) => columnItem?.answer?.validityHint),
      ),
    };
  });
}
